// === webcoding App — TypeScript migration (Phase 2) ===
// This file is the de-IIFEd body of app.js, incrementally split into modules.
// @ts-nocheck — suppress type errors during migration
// Functions already extracted: see src/utils.ts, src/ws/events.ts, src/ui/git.ts

import { generateUUID, deepClone, formatFileSize, escapeHtml, normalizeSafeHref, timeAgo } from './utils';
import { AGENT_LABELS, CUSTOM_MODEL_OPTION_VALUE, DEFAULT_AGENT } from './constants';
import { emitWsEvent, clearWsEvent, onWsEvent } from './ws/events';
import { logCodexSteeringDebug } from './ui/session-debug';
import { showToast, showBrowserNotification, requestNotificationPermission, setToastSessionOpener } from './ui/toast';
import { isMobileInputMode } from './ui/composer-focus';
import { connect, send, scheduleReconnect, setupConnectionHandlers, waitForSocketOpen } from './ws/connection';
import { normalizeAgent, getAgentSessionStorageKey, getAgentModeStorageKey, getLastSessionForAgent, setLastSessionForAgent, getSessionMeta, cloneMessages, estimateSessionMessageWeight, estimateSessionSnapshotWeight, normalizeActiveRuntime, normalizeSessionSnapshot, touchSessionCache, invalidateSessionCache, pruneSessionCache, cacheSessionSnapshot, updateCachedSession, reconcileSessionCacheWithSessions, getSessionCacheDisposition, buildCachedSessionSnapshot } from './session/cache';
import { setupMessageHandlers, handleServerMessage } from './ws/handlers';
import {
  setupGitHandlers, setupGitPanelResizeListeners, syncGitPanelWidthForViewport,
  resetGitState, getGitRepoLabel, sendGitCommand, requestGitStatus,
  buildGitInsightsCardMarkup, toggleGitPanel, openGitPanel, closeGitPanel, renderGitPanel,
  updateGitPanelBadge, showGitTextModal, showGitLogModal, showGitDiffModal,
  performGitAdd, showGitCommitModal, showGitConfirmModal, showGitBranchPicker, promptCreateGitBranch,
  ensureGitPanelResizer, canResizeGitPanel,
  handleGitPanelResizeStart, handleGitPanelResizeMove, handleGitPanelResizeEnd,
} from './ui/git';
import {
  gitState,
  connectionState as sharedConnectionState,
  sessionState as sharedSessionState,
  composeState as sharedComposeState,
} from './state';
import {
  setupSessionManager,
  setCurrentSessionRunningState, restoreInitialSession, resetChatView,
  applySessionSnapshot, setSessionLoading, clearSessionLoading, isBlockingSessionLoad,
  finishSessionSwitch, finalizeLoadedSession, openSession,
} from './session/manager';
import {
  setupSidebarHandlers,
  getSidebarWidthLimit, clampSidebarWidth, applySidebarWidth, canResizeSidebar, syncSidebarWidthForViewport,
  getSessionUpdatedTimestamp,
  normalizeComparablePath, getPathLeaf, decodeClaudeProjectDir, formatClaudeImportGroupPath,
  isSameOrChildPath, findBestProjectForSession, buildVirtualProjectFromSession,
  focusProjectGroup, renderSessionList, highlightActiveSession, updateSessionItemLoadedMessageCount,
  openSidebar, closeSidebar,
  handleSidebarSwipeStart, handleSidebarSwipeMove, handleSidebarSwipeEnd, clearSidebarSwipe,
  handleSidebarResizeStart, handleSidebarResizeMove, handleSidebarResizeEnd,
} from './ui/sidebar';
import {
  setupChatHandlers, setupScrollbar,
  startGenerating, startNextStreamingTurn, finishGenerating,
  scheduleRender, flushRender,
  renderStreamingSegments, markStreamingProcessTextSegments,
  updateStreamingAssistantMeta,
  ensureStreamingPlaceholderVisible,
  renderMessages, prependHistoryMessages,
  appendToolCall, updateToolCall,
  appendSystemMessage, appendError, appendUserMessage,
  scrollToBottom, updateScrollbar,
  incrementRenderEpoch, clearPreviewCode,
} from './ui/chat';
import {
  setupSettingsHandlers,
  showForceChangePassword,
  handlePasswordChanged,
  handleForcedLogout,
  showSettingsPanel,
  showNewSessionModal,
  showImportSessionModal,
  showImportCodexSessionModal,
  applyTheme,
  getStoredTheme,
  restoreRememberedPasswordInput,
  saveRememberedPassword,
  clearRememberedPassword,
  getNativeSessions,
  getCodexSessions,
  getCwdSuggestions,
  getDirectoryListing,
} from './ui/settings';
import { createSessionExportController } from './ui/session-export';
import { createQuickMessagesController } from './ui/quick-messages';
import { createVoiceInputController } from './ui/voice-input';
import { createWorkspaceUiController } from './ui/workspace-insights';
import { setupAgentControlMenu, type AgentControlMenuApi } from './ui/agent-control-menu';
import { createCommandPickerController } from './ui/command-picker';
import { buildSystemCommandPayload } from './command-payload';
import { createAttachmentsController } from './ui/attachments';
import { createAppLifecycleController } from './ui/app-lifecycle';
import { bindComposerUi } from './ui/composer-bindings';
import { bindModelPickerButton } from './ui/model-picker';
import { createChatOutlineController } from './ui/chat-outline';
import { buildMessageAnchor, hasMessageAnchor } from './ui/message-anchor';
import { logAssistantRebuildUiClick, logAssistantRebuildUiSend } from './ui/message-meta-debug';


'use strict';
window.addEventListener('error', (e) => { console.error('[WEBCODING-INIT-ERROR]', e.message, e.filename, 'lineno=', e.lineno); });

const WS_URL = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;

// Claude Code model entries — matches real /model output
const CLAUDE_MODEL_ENTRIES = [
  { alias: 'default', value: 'default', label: '默认（推荐）', desc: '使用默认模型（当前为 Sonnet 4.6）', pricing: '输入/输出 $3 / $15 / 百万 Token' },
  { alias: 'sonnet[1m]', value: 'sonnet[1m]', label: 'Sonnet（1M 上下文）', desc: 'Sonnet 4.6，适合长上下文会话', pricing: '输入/输出 $3 / $15 / 百万 Token' },
  { alias: 'opus', value: 'opus', label: 'Opus', desc: 'Opus 4.6，复杂任务能力最强', pricing: '输入/输出 $5 / $25 / 百万 Token' },
  { alias: 'opus[1m]', value: 'opus[1m]', label: 'Opus（1M 上下文）', desc: 'Opus 4.6，支持 1M 上下文，适合复杂任务', pricing: '输入/输出 $5 / $25 / 百万 Token' },
  { alias: 'haiku', value: 'haiku', label: 'Haiku', desc: 'Haiku 4.5，响应最快，适合快速问答', pricing: '输入/输出 $1 / $5 / 百万 Token' },
];

const SLASH_COMMANDS = [
  { cmd: '/clear', desc: '清除当前会话' },
  { cmd: '/model', desc: '查看/切换模型' },
  { cmd: '/mode', desc: '查看/切换权限模式' },
  { cmd: '/cost', desc: '查看会话费用' },
  { cmd: '/compact', desc: '压缩上下文' },
  { cmd: '/help', desc: '显示帮助' },
];

const MODE_LABELS = {
  default: '默认',
  plan: 'Plan',
  yolo: 'YOLO',
};

const SESSION_CACHE_LIMIT = 64;
const SESSION_CACHE_MAX_WEIGHT = 1_500_000;
const SIDEBAR_DEFAULT_WIDTH = 320;
const VISIBILITY_RESYNC_THROTTLE_MS = 2500;
const INPUT_MAX_HEIGHT_FALLBACK = 200;
const RECONNECT_MAX_ATTEMPTS = 8;
const REMEMBERED_PASSWORD_STORAGE_KEY = 'webcoding-remembered-password';
const THEME_STORAGE_KEY = 'webcoding-theme';
const QUICK_MESSAGE_STORAGE_KEY = 'webcoding-quick-messages';
const QUICK_MESSAGE_LIMIT = 24;
const DEFAULT_THEME = 'default';
const THEME_LABELS = {
  default: '默认主题',
  localhost: '极简主题',
};

const MODE_PICKER_OPTIONS = [
  { value: 'yolo', label: 'YOLO', desc: '跳过所有权限检查' },
  { value: 'plan', label: 'Plan', desc: '执行前需确认计划' },
  { value: 'default', label: '默认', desc: '标准权限审批' },
];


// --- State ---
let ws = null;
let authToken = localStorage.getItem('webcoding-token');
let pendingAuthPassword = null;
let pendingPasswordChangeValue = null;
let currentSessionId = null;
let sessions = [];
let sessionCache = new Map();
let isGenerating = false;
let reconnectAttempts = 0;
let reconnectTimer = null;
let pendingText = '';
let activeToolCalls = new Map();
let currentMode = 'yolo';
let currentModel = '';
let currentActiveRuntime = null;
let currentRuntimeCount = 0;
const savedAgent = localStorage.getItem('webcoding-agent');
let selectedAgent = AGENT_LABELS[savedAgent] ? savedAgent : DEFAULT_AGENT;
let currentAgent = selectedAgent;
let modelConfigCache = null;
let codexConfigCache = null;
let loadedHistorySessionId = null;
let activeSessionLoad = null;
let pendingAttachments = [];
let uploadingAttachments = [];
let currentCwd = null;
let currentSessionRunning = false;
let skipDeleteConfirm = localStorage.getItem('webcoding-skip-delete-confirm') === '1';
let pendingInitialSessionLoad = false;
let projects = [];
let lastVisibilityResyncAt = 0;
let localIdCounter = 0;
let cachedInputMaxHeight = INPUT_MAX_HEIGHT_FALLBACK;
let pendingProjectSaveCallback = null;
let pendingOutgoingMessage = null;
const sessionExportSnapshots = new Map();
// gitState imported from state.ts below

// Stage-1 state grouping: keep legacy vars as source of truth, expose grouped accessors.
const connectionState = {
  get ws() { return sharedConnectionState.ws; },
  set ws(value) { ws = value; sharedConnectionState.ws = value; },
  get reconnectAttempts() { return sharedConnectionState.reconnectAttempts; },
  set reconnectAttempts(value) { reconnectAttempts = value; sharedConnectionState.reconnectAttempts = value; },
  get reconnectTimer() { return sharedConnectionState.reconnectTimer; },
  set reconnectTimer(value) { reconnectTimer = value; sharedConnectionState.reconnectTimer = value; },
  get pendingInitialSessionLoad() { return sharedConnectionState.pendingInitialSessionLoad; },
  set pendingInitialSessionLoad(value) { pendingInitialSessionLoad = value; sharedConnectionState.pendingInitialSessionLoad = value; },
  get authToken() { return sharedConnectionState.authToken; },
  set authToken(value) { authToken = value; sharedConnectionState.authToken = value; },
};

const sessionState = {
  get currentSessionId() { return sharedSessionState.currentSessionId; },
  set currentSessionId(value) { currentSessionId = value; sharedSessionState.currentSessionId = value; },
  get currentAgent() { return sharedSessionState.currentAgent; },
  set currentAgent(value) { currentAgent = value; sharedSessionState.currentAgent = value; },
  get currentMode() { return sharedSessionState.currentMode; },
  set currentMode(value) { currentMode = value; sharedSessionState.currentMode = value; },
  get currentModel() { return sharedSessionState.currentModel; },
  set currentModel(value) { currentModel = value; sharedSessionState.currentModel = value; },
  get currentActiveRuntime() { return sharedSessionState.currentActiveRuntime; },
  set currentActiveRuntime(value) { currentActiveRuntime = value; sharedSessionState.currentActiveRuntime = value; },
  get currentRuntimeCount() { return sharedSessionState.currentRuntimeCount; },
  set currentRuntimeCount(value) { currentRuntimeCount = value; sharedSessionState.currentRuntimeCount = value; },
  get sessions() { return sharedSessionState.sessions; },
  set sessions(value) { sessions = value; sharedSessionState.sessions = value; },
  get sessionCache() { return sharedSessionState.sessionCache; },
  set sessionCache(value) { sessionCache = value; sharedSessionState.sessionCache = value; },
  get loadedHistorySessionId() { return sharedSessionState.loadedHistorySessionId; },
  set loadedHistorySessionId(value) { loadedHistorySessionId = value; sharedSessionState.loadedHistorySessionId = value; },
  get activeSessionLoad() { return sharedSessionState.activeSessionLoad; },
  set activeSessionLoad(value) { activeSessionLoad = value; sharedSessionState.activeSessionLoad = value; },
  get currentHistoryTotal() { return sharedSessionState.currentHistoryTotal; },
  set currentHistoryTotal(value) { sharedSessionState.currentHistoryTotal = value; },
  get currentHistoryBuffered() { return sharedSessionState.currentHistoryBuffered; },
  set currentHistoryBuffered(value) { sharedSessionState.currentHistoryBuffered = value; },
  get currentHistoryPending() { return sharedSessionState.currentHistoryPending; },
  set currentHistoryPending(value) { sharedSessionState.currentHistoryPending = value; },
  get currentHistoryLoading() { return sharedSessionState.currentHistoryLoading; },
  set currentHistoryLoading(value) { sharedSessionState.currentHistoryLoading = value; },
  get currentCwd() { return sharedSessionState.currentCwd; },
  set currentCwd(value) { currentCwd = value; sharedSessionState.currentCwd = value; },
  get currentSessionRunning() { return sharedSessionState.currentSessionRunning; },
  set currentSessionRunning(value) { currentSessionRunning = value; sharedSessionState.currentSessionRunning = value; },
};

const composeState = {
  get isGenerating() { return sharedComposeState.isGenerating; },
  set isGenerating(value) { isGenerating = value; sharedComposeState.isGenerating = value; },
  get pendingText() { return sharedComposeState.pendingText; },
  set pendingText(value) { pendingText = value; sharedComposeState.pendingText = value; },
  get activeToolCalls() { return sharedComposeState.activeToolCalls; },
  set activeToolCalls(value) { activeToolCalls = value; sharedComposeState.activeToolCalls = value; },
  get pendingAttachments() { return sharedComposeState.pendingAttachments; },
  set pendingAttachments(value) { pendingAttachments = value; sharedComposeState.pendingAttachments = value; },
  get uploadingAttachments() { return sharedComposeState.uploadingAttachments; },
  set uploadingAttachments(value) { uploadingAttachments = value; sharedComposeState.uploadingAttachments = value; },
};

// --- DOM ---
const $ = (sel) => document.querySelector(sel);
const loginOverlay = $('#login-overlay');
const loginForm = $('#login-form');
const loginPassword = $('#login-password');
const loginError = $('#login-error');
const loginSubmitBtn = loginForm?.querySelector('button[type="submit"]');
const rememberPw = $('#remember-pw');
const app = $('#app');
const sessionLoadingOverlay = $('#session-loading-overlay');
const sessionLoadingLabel = $('#session-loading-label');
const sidebar = $('#sidebar');
const sidebarResizer = $('#sidebar-resizer');
const sidebarOverlay = $('#sidebar-overlay');
const menuBtn = $('#menu-btn');
const workspaceMain = document.querySelector('.workspace-main');
const chatMain = document.querySelector('.chat-main');
const workspaceInsights = $('#workspace-insights');
const workspaceInsightsContent = $('#workspace-insights-content');
const gitPanelEl = $('#git-panel');
const gitPanelContent = $('#git-panel-content');
const gitPanelBtn = $('#git-panel-btn');
const newChatSplit = sidebar.querySelector('.new-chat-split');
const newChatBtn = $('#new-chat-btn');
const newChatArrow = $('#new-chat-arrow');
const newChatDropdown = $('#new-chat-dropdown');
const importSessionBtn = $('#import-session-btn');
const sessionList = $('#session-list');
const chatTitle = $('#chat-title');
const chatAgentContext = $('#chat-agent-context');
const chatOutlineBtn = $('#chat-outline-btn');
const chatCwd = $('#topbar-chat-cwd');
const costDisplay = $('#topbar-cost-display');
const agentControlBtn = $('#agent-control-btn');
const agentControlMenuEl = $('#agent-control-menu');
const exportSessionBtn = $('#export-session-btn');
const attachmentTray = $('#attachment-tray');
const quickMessagePanel = $('#quick-message-panel');
const quickMessageList = $('#quick-message-list');
const quickMessageToggle = $('#quick-message-toggle');
const commandToggle = $('#command-toggle');
const saveQuickMessageBtn = $('#save-quick-message-btn');
const quickMessageGroupTabs = $('#quick-message-group-tabs');
const addQuickMessageGroupBtn = $('#add-quick-message-group-btn');
const exportQuickMessagesBtn = $('#export-quick-messages-btn');
const importQuickMessagesBtn = $('#import-quick-messages-btn');
const importQuickMessagesInput = $('#import-quick-messages-input') as HTMLInputElement;
const voiceInputBtn = $('#voice-input-btn');
const modelPickerBtn = $('#model-picker-btn');
const modelPickerLabel = $('#model-picker-label');
const imageUploadInput = $('#image-upload-input');
const attachBtn = $('#attach-btn');
const msgInput = $('#msg-input');
const inputWrapper = msgInput.closest('.input-wrapper');
const sendBtn = $('#send-btn');
const abortBtn = $('#abort-btn');
const cmdMenu = $('#cmd-menu');
const modeSelect = $('#mode-select');
let loginPending = false;
let loginAttemptToken = 0;
let loginTimeoutId = null;
let agentControlMenu: AgentControlMenuApi | null = null;

function nextLocalId(prefix = 'tmp') {
  localIdCounter += 1;
  return `${prefix}-${Date.now()}-${localIdCounter}`;
}

function setPendingOutgoingMessage(payload) {
  pendingOutgoingMessage = payload ? { ...payload } : null;
}

function clearPendingOutgoingMessage(clientMessageId = '') {
  if (!pendingOutgoingMessage) return;
  if (clientMessageId && pendingOutgoingMessage.clientMessageId !== clientMessageId) return;
  pendingOutgoingMessage = null;
}

function maybeConfirmPendingOutgoingMessage(messages = []) {
  if (!pendingOutgoingMessage || !Array.isArray(messages)) return;
  const matched = messages.some((message) => message?.role === 'user' && message?.clientMessageId === pendingOutgoingMessage.clientMessageId);
  if (matched) {
    clearPendingOutgoingMessage(pendingOutgoingMessage.clientMessageId);
  }
}

function retryPendingOutgoingMessage(reason = 'retry') {
  if (!pendingOutgoingMessage) return;
  if (!connectionState.ws || connectionState.ws.readyState !== WebSocket.OPEN) return;
  if (sessionState.currentSessionId && pendingOutgoingMessage.sessionId && sessionState.currentSessionId !== pendingOutgoingMessage.sessionId) return;
  send({
    type: 'message',
    text: pendingOutgoingMessage.text,
    attachments: pendingOutgoingMessage.attachments,
    sessionId: pendingOutgoingMessage.sessionId,
    mode: pendingOutgoingMessage.mode,
    agent: pendingOutgoingMessage.agent,
    clientMessageId: pendingOutgoingMessage.clientMessageId,
    retryReason: reason,
  });
}

function refreshInputMaxHeightCache() {
  const raw = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--input-max-height'), 10);
  cachedInputMaxHeight = Number.isFinite(raw) && raw > 0 ? raw : INPUT_MAX_HEIGHT_FALLBACK;
}

function createOverlayPanel({
  overlayClass = 'settings-overlay',
  overlayId = '',
  panelClass = 'settings-panel',
  zIndex = '',
  maxWidth = '',
  panelHtml = '',
} = {}) {
  const overlay = document.createElement('div');
  overlay.className = overlayClass;
  if (overlayId) overlay.id = overlayId;
  if (zIndex) overlay.style.zIndex = String(zIndex);

  const panel = document.createElement('div');
  panel.className = panelClass;
  if (maxWidth) panel.style.maxWidth = maxWidth;
  if (panelHtml) panel.innerHTML = panelHtml;

  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  return {
    overlay,
    panel,
    close: () => overlay.remove(),
  };
}

function getDeleteConfirmMessage(agent) {
  const normalized = normalizeAgent(agent);
  if (normalized === 'codex') {
    return '删除本会话将同步删去本地 Codex rollout 历史与线程记录，不可恢复。确认删除？';
  }
  if (normalized === 'opencode') {
    return '删除本会话将删除 webcoding 中保存的 OpenCode 会话映射与消息历史，不可恢复。确认删除？';
  }
  if (normalized === 'genericagent') {
    return '删除本会话将删除 webcoding 中保存的 GenericAgent 会话映射与消息历史，不会自动删除项目 .ga-temp。确认删除？';
  }
  if (normalized === 'hermes') {
    return '删除本会话将删除 webcoding 中保存的 Hermes 会话映射与消息历史，不会自动删除 Hermes 原生记录。确认删除？';
  }
  return '删除本会话将同步删去本地 Claude 中的会话历史，不可恢复。确认删除？';
}

function showDeleteConfirm(agent, onConfirm) {
  const { overlay, panel: box, close } = createOverlayPanel({
    zIndex: '10002',
    panelHtml: `
      <div style="font-size:0.9em;color:var(--text-primary);margin-bottom:20px;line-height:1.7">${escapeHtml(getDeleteConfirmMessage(agent))}</div>
      <div style="display:flex;flex-direction:column;gap:8px">
        <button id="del-confirm-ok" style="width:100%;padding:10px;border:none;border-radius:10px;background:var(--accent);color:#fff;font-size:0.95em;font-weight:600;cursor:pointer;font-family:inherit">确认删除</button>
        <button id="del-confirm-skip" style="width:100%;padding:9px;border:1px solid var(--border-color);border-radius:10px;background:var(--bg-tertiary);color:var(--text-secondary);font-size:0.85em;cursor:pointer;font-family:inherit">确认且不再提示</button>
        <button id="del-confirm-cancel" style="width:100%;padding:9px;border:none;border-radius:10px;background:transparent;color:var(--text-muted);font-size:0.85em;cursor:pointer;font-family:inherit">取消</button>
      </div>
    `,
  });

  box.querySelector('#del-confirm-ok').addEventListener('click', () => {
    close();
    onConfirm();
  });
  box.querySelector('#del-confirm-skip').addEventListener('click', () => {
    skipDeleteConfirm = true;
    localStorage.setItem('webcoding-skip-delete-confirm', '1');
    close();
    onConfirm();
  });
  box.querySelector('#del-confirm-cancel').addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
}


// --- Viewport height fix for mobile browsers ---
function setVH() {
  document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
}

setVH();
refreshInputMaxHeightCache();
applyTheme(getStoredTheme(), { skipPersist: true });
window.addEventListener('resize', setVH);
window.addEventListener('orientationchange', () => setTimeout(setVH, 100));
window.addEventListener('resize', refreshInputMaxHeightCache);
window.addEventListener('orientationchange', () => setTimeout(refreshInputMaxHeightCache, 100));
syncSidebarWidthForViewport();
syncGitPanelWidthForViewport();
window.addEventListener('resize', syncSidebarWidthForViewport);
window.addEventListener('resize', syncGitPanelWidthForViewport);

// --- Session agent helpers + cache → src/session/cache.ts ---

// --- WS Event Bus → src/ws/events.ts ---

// --- Session cache → src/session/cache.ts ---

function getVisibleSessions() {
  return sessionState.sessions;
}

function getSavedModeForAgent(agent) {
  return localStorage.getItem(getAgentModeStorageKey(agent)) || 'yolo';
}

function getCurrentExportSnapshot() {
  if (!sessionState.currentSessionId) return null;
  const cached = buildCachedSessionSnapshot(sessionState.currentSessionId);
  const fallback = sessionExportSnapshots.get(sessionState.currentSessionId);
  if (cached && fallback) {
    const cachedCount = Array.isArray(cached.messages) ? cached.messages.length : 0;
    const fallbackCount = Array.isArray(fallback.messages) ? fallback.messages.length : 0;
    return cachedCount >= fallbackCount ? cached : deepClone(fallback);
  }
  if (cached) return cached;
  return fallback ? deepClone(fallback) : null;
}

const sessionExportController = createSessionExportController({
  getCurrentSessionId: () => sessionState.currentSessionId,
  getCurrentSnapshot: () => getCurrentExportSnapshot(),
  appendError: (message) => appendError(message),
  showToast: (message) => showToast(message),
  createOverlayPanel: (options) => createOverlayPanel(options),
});
createChatOutlineController({
  button: chatOutlineBtn,
  messages: document.getElementById('messages'),
});
const quickMessagesController = createQuickMessagesController({
  storageKey: QUICK_MESSAGE_STORAGE_KEY,
  limit: QUICK_MESSAGE_LIMIT,
  getAuthToken: () => connectionState.authToken,
  send: (payload) => send(payload),
  showToast: (message) => showToast(message),
  panel: quickMessagePanel,
  list: quickMessageList,
  toggle: quickMessageToggle,
  saveButton: saveQuickMessageBtn,
  groupTabs: quickMessageGroupTabs,
  addGroupButton: addQuickMessageGroupBtn,
  exportButton: exportQuickMessagesBtn,
  importButton: importQuickMessagesBtn,
  importInput: importQuickMessagesInput,
  input: msgInput,
  autoResize: () => autoResize(),
  sendMessage: () => sendMessage(),
});
const voiceInputController = createVoiceInputController({
  input: msgInput,
  button: voiceInputBtn,
  autoResize: () => autoResize(),
  onInputUpdated: () => quickMessagesController.render(),
  showToast: (message) => showToast(message),
});
const workspaceUiController = createWorkspaceUiController({
  workspaceInsightsContent,
  getProjects: () => projects,
  getVisibleSessions: () => getVisibleSessions(),
  getCurrentSessionId: () => sessionState.currentSessionId,
  getSessionMeta: (sessionId) => getSessionMeta(sessionId),
  findBestProjectForSession,
  buildVirtualProjectFromSession,
  decodeClaudeProjectDir,
  isSameOrChildPath,
  buildCachedSessionSnapshot,
  getMessageDomCount: () => document.getElementById('messages') ? document.getElementById('messages').querySelectorAll('.msg').length : 0,
  getUsageText: () => costDisplay?.textContent || '',
  getSelectedAgent: () => selectedAgent,
  getCurrentAgent: () => sessionState.currentAgent,
  getCurrentMode: () => sessionState.currentMode,
  getCurrentModel: () => sessionState.currentModel,
  getCurrentActiveRuntime: () => sessionState.currentActiveRuntime,
  getCurrentRuntimeCount: () => sessionState.currentRuntimeCount,
  isCurrentSessionRunning: () => sessionState.currentSessionRunning,
  agentLabels: AGENT_LABELS,
  modeLabels: MODE_LABELS,
  escapeHtml,
  timeAgo,
});
const commandPickerController = createCommandPickerController({
  cmdMenu,
  commandToggle,
  chatMain,
  slashCommands: SLASH_COMMANDS,
  claudeModelEntries: CLAUDE_MODEL_ENTRIES,
  modePickerOptions: MODE_PICKER_OPTIONS,
  escapeHtml,
  getCurrentAgent: () => sessionState.currentAgent,
  getCurrentSessionId: () => sessionState.currentSessionId,
  getCurrentMode: () => sessionState.currentMode,
  sendMessagePayload: (payload) => send(payload),
  onModeSelected: (value) => handleModeSelectionChange(value),
});
const modelPickerController = bindModelPickerButton({
  button: modelPickerBtn,
  labelEl: modelPickerLabel,
  sessionState,
  selectedAgentProvider: () => selectedAgent,
  showOptionPicker: (title, options, currentValue, onSelect) => showOptionPicker(title, options, currentValue, onSelect),
  send: (payload) => send(payload),
});
const attachmentsController = createAttachmentsController({
  attachmentTray,
  attachButton: attachBtn,
  imageUploadInput,
  composeState,
  getAuthToken: () => connectionState.authToken,
  getSocketState: () => connectionState.ws,
  connect: () => connect(),
  send: (payload) => send(payload),
  appendError: (message) => appendError(message),
  formatFileSize,
  escapeHtml,
  nextLocalId: (prefix) => nextLocalId(prefix),
});
const appLifecycleController = createAppLifecycleController({
  loginForm,
  loginPassword,
  loginError,
  loginOverlay,
  app,
  rememberPw,
  getLoginPending: () => loginPending,
  setLoginPendingState: (pending, message = '') => setLoginPendingState(pending, message),
  clearLoginPendingState: () => clearLoginPendingState(),
  getLoginAttemptToken: () => loginAttemptToken,
  setLoginAttemptToken: (value) => { loginAttemptToken = value; },
  setPendingAuthPassword: (value) => { pendingAuthPassword = value; },
  setLoginTimeoutId: (value) => { loginTimeoutId = value; },
  waitForSocketOpen: () => waitForSocketOpen(),
  getSocket: () => connectionState.ws,
  send: (payload) => send(payload),
  requestNotificationPermission,
  clearRememberedPassword,
  restoreRememberedPasswordInput,
  connect: () => connect(),
  connectionState,
  sessionState,
  composeState,
  getPendingOutgoingMessage: () => pendingOutgoingMessage,
  retryPendingOutgoingMessage: (reason) => retryPendingOutgoingMessage(reason),
  sessionExportSnapshots,
  deepClone,
  maybeConfirmPendingOutgoingMessage: (messages) => maybeConfirmPendingOutgoingMessage(messages),
  openSession: (id, options) => openSession(id, options),
  getLastVisibilityResyncAt: () => lastVisibilityResyncAt,
  setLastVisibilityResyncAt: (value) => { lastVisibilityResyncAt = value; },
  visibilityResyncThrottleMs: VISIBILITY_RESYNC_THROTTLE_MS,
  updateCwdBadge: () => updateCwdBadge(),
  updateSessionItemLoadedMessageCount: (sessionId) => updateSessionItemLoadedMessageCount(sessionId),
  rememberedPasswordStorageKey: REMEMBERED_PASSWORD_STORAGE_KEY,
});

function renderWorkspaceInsights() {
  workspaceUiController.renderWorkspaceInsights();
}

function buildWelcomeMarkup(agent) {
  return workspaceUiController.buildWelcomeMarkup(agent);
}

function buildWorkspaceActionButtons(actions, options = {}) {
  return workspaceUiController.buildWorkspaceActionButtons(actions, options);
}

function getCurrentProjectContext() {
  return workspaceUiController.getCurrentProjectContext();
}

function getCurrentProjectSessionCount(project) {
  return workspaceUiController.getCurrentProjectSessionCount(project);
}

function showCmdMenu(filter) {
  commandPickerController.showCmdMenu(filter);
}

function hideCmdMenu() {
  commandPickerController.hideCmdMenu();
}

function navigateCmdMenu(direction) {
  commandPickerController.navigateCmdMenu(direction);
}

function selectCmdMenuItem() {
  commandPickerController.selectCmdMenuItem();
}

function showOptionPicker(title, options, currentValue, onSelect) {
  commandPickerController.showOptionPicker(title, options, currentValue, onSelect);
}

function hideOptionPicker() {
  commandPickerController.hideOptionPicker();
}

function showModelPicker() {
  commandPickerController.showModelPicker();
}

function showClaudeModelPicker(menuEntries, models, currentAlias, currentFull) {
  commandPickerController.showClaudeModelPicker(menuEntries, models, currentAlias, currentFull);
}

function showModePicker() {
  commandPickerController.showModePicker();
}

function renderAttachmentLabels(attachments, options = {}) {
  return attachmentsController.renderAttachmentLabels(attachments, options);
}

function renderPendingAttachments() {
  attachmentsController.renderPendingAttachments();
}

async function handleSelectedImageFiles(fileList) {
  await attachmentsController.handleSelectedImageFiles(fileList);
}

function updateAgentScopedUI() {
  const selectedAgentLabel = AGENT_LABELS[selectedAgent] || selectedAgent;
  const currentAgentLabel = sessionState.currentSessionId ? (AGENT_LABELS[sessionState.currentAgent] || sessionState.currentAgent) : '未开始';
  document.querySelectorAll('.agent-tab').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.agent === selectedAgent);
  });
  document.querySelectorAll('.mode-tab').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.mode === sessionState.currentMode);
  });
  const mas = document.getElementById('mobile-agent-select');
  const mms = document.getElementById('mobile-mode-select');
  if (mas) mas.value = selectedAgent;
  if (mms) mms.value = sessionState.currentMode;
  if (importSessionBtn) {
    importSessionBtn.textContent = selectedAgent === 'codex'
      ? '导入本地 Codex 会话'
      : selectedAgent === 'opencode'
        ? 'OpenCode 暂不支持导入'
        : selectedAgent === 'genericagent'
          ? 'GenericAgent 暂不支持导入'
        : selectedAgent === 'hermes'
          ? 'Hermes 暂不支持导入'
        : '导入本地 Claude 会话';
    importSessionBtn.disabled = selectedAgent === 'opencode' || selectedAgent === 'genericagent' || selectedAgent === 'hermes';
  }
  if (chatAgentContext) {
    chatAgentContext.textContent = `当前对话：${currentAgentLabel} · 新建默认：${selectedAgentLabel}`;
  }
  if (exportSessionBtn) {
    exportSessionBtn.disabled = !sessionState.currentSessionId;
  }
  modelPickerController.setLabel(sessionState.currentModel);
  agentControlMenu?.update();
}

function setSelectedAgent(agent, options = {}) {
  selectedAgent = normalizeAgent(agent);
  localStorage.setItem('webcoding-agent', selectedAgent);
  if (options.syncMode) {
    sessionState.currentMode = getSavedModeForAgent(selectedAgent);
    modeSelect.value = sessionState.currentMode;
  }
  updateAgentScopedUI();
}

function setCurrentAgent(agent) {
  sessionState.currentAgent = normalizeAgent(agent);
  updateAgentScopedUI();
}

function handleAgentSelectionChange(agent) {
  const targetAgent = normalizeAgent(agent);
  if (targetAgent === selectedAgent) return;
  const hadOpenSession = !!sessionState.currentSessionId;
  setSelectedAgent(targetAgent, { syncMode: !hadOpenSession });
  if (!hadOpenSession) {
    resetChatView(targetAgent);
    return;
  }
  renderSessionList();
  highlightActiveSession();
  renderWorkspaceInsights();
}

function handleModeSelectionChange(mode) {
  sessionState.currentMode = mode;
  modeSelect.value = mode;
  localStorage.setItem(getAgentModeStorageKey(sessionState.currentAgent), sessionState.currentMode);
  updateAgentScopedUI();
  if (sessionState.currentSessionId) send({ type: 'set_mode', sessionId: sessionState.currentSessionId, mode: sessionState.currentMode });
  renderWorkspaceInsights();
}


function shouldOverlayRuntimeBadge() {
  return window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;
}

function updateCwdBadge() {
  if (!chatCwd) return;
  if (sessionState.currentCwd) {
    const parts = sessionState.currentCwd.replace(/\/+$/, '').split('/');
    const short = parts.slice(-2).join('/') || sessionState.currentCwd;
    chatCwd.textContent = '~/' + short;
    chatCwd.title = sessionState.currentCwd;
  } else {
    chatCwd.textContent = '';
    chatCwd.title = '';
  }
  chatCwd.hidden = !sessionState.currentCwd || (sessionState.currentSessionRunning && shouldOverlayRuntimeBadge());
}

function setStatsDisplay(msg) {
  if ((sessionState.currentAgent === 'codex' || sessionState.currentAgent === 'opencode') && msg && msg.totalUsage) {
    const usage = msg.totalUsage;
    if ((usage.inputTokens || 0) > 0 || (usage.outputTokens || 0) > 0) {
      const cacheText = usage.cachedInputTokens ? ` · cache ${usage.cachedInputTokens}` : '';
      costDisplay.textContent = `in ${usage.inputTokens} · out ${usage.outputTokens}${cacheText}`;
      return;
    }
  }
  if (msg && typeof msg.totalCost === 'number' && msg.totalCost > 0) {
    costDisplay.textContent = `$${msg.totalCost.toFixed(4)}`;
    return;
  }
  costDisplay.textContent = '';
}

function getCodexModelOptions() {
  const seen = new Set();
  const options = [];

  function addOption(value, label, desc) {
    const v = (value || '').trim();
    if (!v || seen.has(v)) return;
    seen.add(v);
    options.push({ value: v, label: label || v, desc: desc || 'Codex 模型' });
  }

  addOption('default', '默认模型', '使用当前默认模型');
  addOption(sessionState.currentModel, sessionState.currentModel, '当前会话模型');
  sessionState.sessions
    .filter((s) => normalizeAgent(s.agent) === 'codex')
    .forEach((s) => addOption(s.model, s.model, s.id === sessionState.currentSessionId ? '当前会话已保存模型' : '其他 Codex 会话模型'));

  options.push({ value: CUSTOM_MODEL_OPTION_VALUE, label: '自定义模型…', desc: '使用系统弹窗输入模型 ID' });

  return options;
}

function getOpenCodeModelOptions() {
  const seen = new Set();
  const options = [];

  function addOption(value, label, desc) {
    const v = (value || '').trim();
    if (!v || seen.has(v)) return;
    seen.add(v);
    options.push({ value: v, label: label || v, desc: desc || 'OpenCode 模型' });
  }

  addOption('default', '默认模型', '使用本机 OpenCode 默认模型');
  addOption(sessionState.currentModel, sessionState.currentModel, '当前会话模型');
  sessionState.sessions
    .filter((s) => normalizeAgent(s.agent) === 'opencode')
    .forEach((s) => addOption(s.model, s.model, s.id === sessionState.currentSessionId ? '当前会话已保存模型' : '其他 OpenCode 会话模型'));

  options.push({ value: CUSTOM_MODEL_OPTION_VALUE, label: '自定义模型…', desc: '使用系统弹窗输入模型 ID' });

  return options;
}



// --- Header title editing (contenteditable) ---


// --- Header title editing (contenteditable) ---
chatTitle.addEventListener('click', () => {
  if (!sessionState.currentSessionId || chatTitle.contentEditable === 'true') return;
  const originalText = chatTitle.textContent;
  chatTitle.contentEditable = 'true';
  chatTitle.style.background = 'var(--chat-title-edit-bg)';
  chatTitle.style.outline = '1px solid var(--accent)';
  chatTitle.style.borderRadius = '6px';
  chatTitle.style.padding = '2px 8px';
  chatTitle.style.minWidth = '96px';
  chatTitle.style.whiteSpace = 'normal';
  chatTitle.style.overflow = 'visible';
  chatTitle.style.textOverflow = 'clip';
  chatTitle.focus();
  // Select all text
  const range = document.createRange();
  range.selectNodeContents(chatTitle);
  const sel = window.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);

  function finish(save) {
    chatTitle.removeEventListener('keydown', handler);
    chatTitle.contentEditable = 'false';
    chatTitle.style.background = '';
    chatTitle.style.outline = '';
    chatTitle.style.borderRadius = '';
    chatTitle.style.padding = '';
    chatTitle.style.minWidth = '';
    chatTitle.style.whiteSpace = '';
    chatTitle.style.overflow = '';
    chatTitle.style.textOverflow = '';
    const newTitle = chatTitle.textContent.trim() || originalText;
    chatTitle.textContent = newTitle;
    if (save && newTitle !== originalText && sessionState.currentSessionId) {
      send({ type: 'rename_session', sessionId: sessionState.currentSessionId, title: newTitle });
    }
  }

  chatTitle.addEventListener('blur', () => finish(true), { once: true });
  function handler(e) {
    if (e.key === 'Enter') { e.preventDefault(); chatTitle.removeEventListener('keydown', handler); chatTitle.blur(); }
    if (e.key === 'Escape') { chatTitle.textContent = originalText; chatTitle.removeEventListener('keydown', handler); chatTitle.blur(); }
  }
  chatTitle.addEventListener('keydown', handler);
});

// --- Send Message ---
function sendMessage() {
  if (voiceInputController.isActive()) voiceInputController.stop({ keepText: true, refocus: false });
  const text = msgInput.value.trim();
  const isPlanModeActive = sessionState.currentMode === 'plan' && sessionState.currentAgent === 'claude';
  const hasRenderedMessages = !!document.querySelector('#messages .msg');
  if ((!text && composeState.pendingAttachments.length === 0) || isBlockingSessionLoad()) return;
  if ((composeState.isGenerating || sessionState.currentSessionRunning) && !isPlanModeActive) {
    if (sessionState.currentAgent === 'codex' && sessionState.currentSessionId) {
      if (composeState.pendingAttachments.length > 0) {
        appendError('Codex 运行中追加消息暂不支持图片附件，请等待当前任务完成后再发送。');
        return;
      }
      const clientMessageId = nextLocalId('steer');
      logCodexSteeringDebug('ui:send', {
        sessionId: sessionState.currentSessionId,
        clientMessageId,
        textLength: text.length,
        agent: sessionState.currentAgent,
        isGenerating: composeState.isGenerating,
      });
      appendUserMessage(text, [], { role: 'user', clientMessageId, timestamp: new Date().toISOString() });
      logCodexSteeringDebug('ui:user-message-appended', {
        sessionId: sessionState.currentSessionId,
        clientMessageId,
        currentSessionRunning: !!sessionState.currentSessionRunning,
        isGenerating: !!composeState.isGenerating,
        domMessageCount: document.querySelectorAll('#messages .msg').length,
        domAssistantCount: document.querySelectorAll('#messages .msg.assistant').length,
        lastDomIdentity: document.querySelector('#messages .msg:last-child')?.getAttribute('data-message-identity') || null,
      });
      updateCachedSession(sessionState.currentSessionId, (snapshot) => {
        const messages = Array.isArray(snapshot.messages) ? snapshot.messages : [];
        const alreadyCached = messages.some((message) => message?.clientMessageId === clientMessageId);
        logCodexSteeringDebug('ui:steer-cache-check', {
          sessionId: sessionState.currentSessionId,
          clientMessageId,
          alreadyCached,
          cachedMessageCount: messages.length,
          cachedUserMessageCount: messages.filter((message) => message?.role === 'user').length,
        });
        if (alreadyCached) return;
        messages.push({
          role: 'user',
          content: text,
          attachments: [],
          timestamp: new Date().toISOString(),
          clientMessageId,
        });
        snapshot.messages = messages;
        snapshot.updated = new Date().toISOString();
        snapshot.isRunning = true;
        snapshot.complete = false;
        logCodexSteeringDebug('ui:steer-cache-upsert', {
          sessionId: sessionState.currentSessionId,
          clientMessageId,
          cachedMessageCount: messages.length,
          cachedUserMessageCount: messages.filter((message) => message?.role === 'user').length,
        });
      });
      const cachedAfterSteer = sessionState.sessionCache.get(sessionState.currentSessionId) as any;
      const cachedAfterSteerMessages = Array.isArray(cachedAfterSteer?.snapshot?.messages)
        ? cachedAfterSteer.snapshot.messages
        : [];
      logCodexSteeringDebug('ui:steer-cache-after-upsert', {
        sessionId: sessionState.currentSessionId,
        clientMessageId,
        cacheEntryPresent: !!cachedAfterSteer,
        cachedMessageCount: cachedAfterSteerMessages.length,
        cachedUserMessageCount: cachedAfterSteerMessages.filter((message: any) => message?.role === 'user').length,
        cachedContainsClientMessage: cachedAfterSteerMessages.some((message: any) => message?.clientMessageId === clientMessageId),
        cachedUpdated: cachedAfterSteer?.snapshot?.updated || cachedAfterSteer?.version || null,
      });
      send({
        type: 'steer',
        text,
        sessionId: sessionState.currentSessionId,
        clientMessageId,
      });
      msgInput.value = '';
      autoResize();
      quickMessagesController.render();
    }
    return;
  }
  hideCmdMenu();
  hideOptionPicker();

  // Guard against transient state desync: do not silently create a brand-new session
  // when the UI still shows an existing conversation.
  if (!sessionState.currentSessionId && hasRenderedMessages) {
    appendError('当前会话状态尚未同步，请在左侧重新点选该会话后重试。');
    return;
  }

  // Regular message
  const attachments = composeState.pendingAttachments.map((attachment) => ({ ...attachment }));
  const clientMessageId = nextLocalId('msg');
  const messageTimestamp = new Date().toISOString();
  const outgoingMessage = {
    role: 'user',
    content: text,
    attachments,
    clientMessageId,
    timestamp: messageTimestamp,
  };
  appendUserMessage(text, attachments, outgoingMessage);
  if (sessionState.currentAgent === 'codex') {
    logCodexSteeringDebug('ui:user-message-appended', {
      sessionId: sessionState.currentSessionId || null,
      clientMessageId,
      currentSessionRunning: !!sessionState.currentSessionRunning,
      isGenerating: !!composeState.isGenerating,
      domMessageCount: document.querySelectorAll('#messages .msg').length,
      domAssistantCount: document.querySelectorAll('#messages .msg.assistant').length,
      lastDomIdentity: document.querySelector('#messages .msg:last-child')?.getAttribute('data-message-identity') || null,
    });
  }
  if (sessionState.currentSessionId) {
    updateCachedSession(sessionState.currentSessionId, (snapshot) => {
      const messages = Array.isArray(snapshot.messages) ? snapshot.messages : [];
      const alreadyCached = messages.some((message: any) => message?.clientMessageId === clientMessageId);
      if (!alreadyCached) messages.push(deepClone(outgoingMessage));
      snapshot.messages = messages;
      snapshot.updated = messageTimestamp;
      snapshot.isRunning = true;
      snapshot.complete = false;
      logCodexSteeringDebug('ui:message-cache-upsert', {
        sessionId: sessionState.currentSessionId,
        clientMessageId,
        alreadyCached,
        cachedMessageCount: messages.length,
        cachedUserMessageCount: messages.filter((message: any) => message?.role === 'user').length,
        cachedLastRole: messages[messages.length - 1]?.role || null,
      });
    });
  }
  setPendingOutgoingMessage({
    clientMessageId,
    text,
    attachments,
    sessionId: sessionState.currentSessionId,
    mode: sessionState.currentMode,
    agent: sessionState.currentAgent,
  });
  scrollToBottom();

  send({ type: 'message', text, command: false, attachments, sessionId: sessionState.currentSessionId, mode: sessionState.currentMode, agent: sessionState.currentAgent, clientMessageId });
  msgInput.value = '';
  composeState.pendingAttachments = [];
  renderPendingAttachments();
  autoResize();
  quickMessagesController.render();
  if (!composeState.isGenerating) startGenerating('user_send');
}

function autoResize() {
  msgInput.style.height = 'auto';
  msgInput.style.height = Math.min(msgInput.scrollHeight, cachedInputMaxHeight) + 'px';
}

const mobileEnterDebug = {
  logInput() {},
  handleBeforeInput() {},
  logKeydown() {},
  logAction() {},
  clearPendingSend() {},
  shouldDeferMobileEnter() { return false; },
  notifyWillSend() {},
  scheduleSend(sendMessage: () => void) { sendMessage(); },
};

function setLoginPendingState(pending, message = '') {
  loginPending = pending;
  if (loginSubmitBtn) {
    loginSubmitBtn.disabled = pending;
    loginSubmitBtn.textContent = pending ? '连接中…' : '进入工作台';
  }
  loginPassword.disabled = pending;
  if (message) {
    loginError.textContent = message;
    loginError.hidden = false;
  } else if (!pending) {
    loginError.textContent = '密码错误';
  }
}

function clearLoginPendingState() {
  if (loginTimeoutId) {
    clearTimeout(loginTimeoutId);
    loginTimeoutId = null;
  }
  setLoginPendingState(false);
}

// --- Event Listeners ---
appLifecycleController.bindLoginHandlers();

menuBtn.addEventListener('click', () => {
  sidebar.classList.contains('open') ? closeSidebar() : openSidebar();
});

sidebarOverlay.addEventListener('click', closeSidebar);
if (sidebarResizer) {
  sidebarResizer.addEventListener('pointerdown', handleSidebarResizeStart);
  sidebarResizer.addEventListener('dblclick', () => applySidebarWidth(SIDEBAR_DEFAULT_WIDTH));
  window.addEventListener('pointermove', handleSidebarResizeMove);
  window.addEventListener('pointerup', handleSidebarResizeEnd);
  window.addEventListener('pointercancel', handleSidebarResizeEnd);
}
setupGitPanelResizeListeners();
document.addEventListener('touchstart', handleSidebarSwipeStart, { passive: true });
document.addEventListener('touchmove', handleSidebarSwipeMove, { passive: false });
document.addEventListener('touchend', handleSidebarSwipeEnd, { passive: true });
document.addEventListener('touchcancel', clearSidebarSwipe, { passive: true });

// Agent tabs
document.querySelectorAll('.agent-tab').forEach((btn) => {
  btn.addEventListener('click', () => {
    const targetAgent = normalizeAgent(btn.dataset.agent);
    handleAgentSelectionChange(targetAgent);
  });
});

// Mobile agent select
const mobileAgentSelect = document.getElementById('mobile-agent-select');
const mobileModeSelect = document.getElementById('mobile-mode-select');
if (mobileAgentSelect) {
  mobileAgentSelect.addEventListener('change', () => {
    const targetAgent = normalizeAgent(mobileAgentSelect.value);
    handleAgentSelectionChange(targetAgent);
  });
}
if (mobileModeSelect) {
  mobileModeSelect.addEventListener('change', () => {
    handleModeSelectionChange(mobileModeSelect.value);
  });
}

if (agentControlBtn && agentControlMenuEl) {
  agentControlMenu = setupAgentControlMenu({
    button: agentControlBtn,
    menu: agentControlMenuEl,
    getSelectedAgent: () => selectedAgent,
    getCurrentMode: () => sessionState.currentMode,
    onAgentSelect: (agent) => handleAgentSelectionChange(agent),
    onModeSelect: (mode) => handleModeSelectionChange(mode),
  });
  agentControlMenu.update();
}

function handleWorkspaceActionClick(actionBtn, event) {
  if (!actionBtn) return;
  event.preventDefault();
  const action = actionBtn.dataset.workspaceAction;
  if (action === 'new-session') {
    showNewSessionModal();
    return;
  }
  if (action === 'import-session') {
    if (selectedAgent === 'codex') {
      showImportCodexSessionModal();
    } else if (selectedAgent === 'opencode') {
      showToast('OpenCode MVP 暂不支持导入本地会话。');
    } else if (selectedAgent === 'genericagent') {
      showToast('GenericAgent 暂不支持从 UI 导入本地会话。');
    } else {
      showImportSessionModal();
    }
    return;
  }
  if (action === 'switch-model') {
    showModelPicker();
    return;
  }
  if (action === 'switch-mode') {
    showModePicker();
    return;
  }
  if (action === 'open-settings') {
    showSettingsPanel();
    return;
  }
  if (action === 'focus-project' && actionBtn.dataset.projectId) {
    focusProjectGroup(actionBtn.dataset.projectId, { toast: '已定位到当前项目。' });
    return;
  }
  if (action === 'refresh-git') {
    requestGitStatus();
    return;
  }
  if (action === 'show-git-diff') {
    showGitDiffModal();
    return;
  }
  if (action === 'show-git-staged-diff') {
    showGitDiffModal({ staged: true });
    return;
  }
  if (action === 'show-git-log') {
    showGitLogModal();
    return;
  }
  if (action === 'git-add-all') {
    performGitAdd('.');
    return;
  }
  if (action === 'git-commit') {
    showGitCommitModal();
    return;
  }
  if (action === 'git-checkout') {
    showGitBranchPicker();
    return;
  }
  if (action === 'git-create-branch') {
    promptCreateGitBranch();
    return;
  }
}

function handleGlobalDocumentClick(event) {
  const target = event.target;

  // Git panel toggle button
  if (target instanceof Element && (target === gitPanelBtn || target.closest('#git-panel-btn'))) {
    toggleGitPanel();
    return;
  }
  // Git panel close button
  if (target instanceof Element && (target.id === 'git-panel-close' || target.closest('#git-panel-close'))) {
    closeGitPanel();
    return;
  }
  // Git panel tab switching
  const gitTab = target instanceof Element ? target.closest('[data-git-view]') : null;
  if (gitTab && gitPanelEl?.contains(gitTab)) {
    event.preventDefault();
    const newView = gitTab.dataset.gitView;
    gitState.activePanelView = newView;
    renderGitPanel();
    if (newView === 'log' && !gitState.panelLogEntries.length) {
      showGitLogModal();
    } else if (newView === 'diff' && !gitState.panelDiffContent) {
      showGitDiffModal();
    }
    return;
  }

  const gitBtn = target instanceof Element ? target.closest('[data-git-action]') : null;
  if (gitBtn) {
    event.preventDefault();
    const gitAction = gitBtn.dataset.gitAction;
    const gitFile = gitBtn.dataset.gitFile || '';
    const gitStaged = gitBtn.dataset.gitStaged === '1';
    if (gitAction === 'show-diff') {
      showGitDiffModal({ file: gitFile, staged: gitStaged });
      return;
    }
    if (gitAction === 'add') {
      performGitAdd(gitFile || '.');
      return;
    }
    if (gitAction === 'expand-files') {
      gitState.filesExpanded = true;
      renderGitPanel();
      renderWorkspaceInsights();
      return;
    }
    if (gitAction === 'toggle-tree-node') {
      const nodeKey = gitBtn.dataset.gitNode || '';
      const collapsed = new Set(Array.isArray(gitState.collapsedTreeNodes) ? gitState.collapsedTreeNodes : []);
      if (collapsed.has(nodeKey)) {
        collapsed.delete(nodeKey);
      } else {
        collapsed.add(nodeKey);
      }
      gitState.collapsedTreeNodes = [...collapsed];
      renderGitPanel();
      return;
    }
  }
  const actionBtn = target instanceof Element ? target.closest('[data-workspace-action]') : null;
  handleWorkspaceActionClick(actionBtn, event);

  if (!newChatDropdown.hidden &&
      !newChatDropdown.contains(target) &&
      target !== newChatArrow) {
    newChatDropdown.hidden = true;
  }

}

document.addEventListener('click', handleGlobalDocumentClick);
bindComposerUi({
  newChatBtn,
  newChatArrow,
  newChatDropdown,
  importSessionBtn,
  exportSessionBtn,
  sendBtn,
  abortBtn,
  voiceInputBtn,
  modelPickerBtn,
  quickMessageToggle,
  commandToggle,
  saveQuickMessageBtn,
  attachBtn,
  imageUploadInput,
  inputWrapper,
  modeSelect,
  msgInput,
  cmdMenu,
  quickMessagesController,
  voiceInputController,
  mobileEnterDebug,
  sessionExportController,
  sessionState,
  composeState,
  selectedAgentProvider: () => selectedAgent,
  isMobileInputMode: () => isMobileInputMode(),
  showNewSessionModal: () => showNewSessionModal(),
  showImportSessionModal: () => showImportSessionModal(),
  showImportCodexSessionModal: () => showImportCodexSessionModal(),
  showModelPicker: () => modelPickerController.open(),
  sendMessage: () => sendMessage(),
  sendAbort: () => send({ type: 'abort', sessionId: sessionState.currentSessionId || null }),
  autoResize: () => autoResize(),
  showCmdMenu: (filter) => showCmdMenu(filter),
  hideCmdMenu: () => hideCmdMenu(),
  navigateCmdMenu: (direction) => navigateCmdMenu(direction),
  selectCmdMenuItem: () => selectCmdMenuItem(),
  handleSelectedImageFiles: (files) => { handleSelectedImageFiles(files); },
  setMode: (mode) => {
    sessionState.currentMode = mode;
    modeSelect.value = mode;
    localStorage.setItem(getAgentModeStorageKey(sessionState.currentAgent), sessionState.currentMode);
    updateAgentScopedUI();
    if (sessionState.currentSessionId) {
      send({ type: 'set_mode', sessionId: sessionState.currentSessionId, mode: sessionState.currentMode });
    }
    renderWorkspaceInsights();
  },
});

onWsEvent('quick_messages_config', (payload) => {
  quickMessagesController.applyFromServer(payload);
});

// --- Toast & Notification → src/ui/toast.ts ---

// --- Helpers → src/utils.ts ---

// --- Init ---
// 依赖注入：将 app.ts 中的函数注入到已提取的模块
setToastSessionOpener((id) => openSession(id));
setupChatHandlers({
  buildWelcomeMarkup: (agent) => buildWelcomeMarkup(agent),
  nextLocalId: (prefix) => nextLocalId(prefix),
  renderAttachmentLabels: (attachments, opts) => renderAttachmentLabels(attachments, opts),
  autoResize: () => autoResize(),
  saveQuickMessage: (text) => quickMessagesController.save(text, { source: 'history' }),
  hasQuickMessage: (text) => quickMessagesController.has(text),
  rebuildCurrentSession: (anchor) => {
    if (sessionState.currentSessionId) {
      const normalizedAnchor = buildMessageAnchor(anchor);
      logAssistantRebuildUiClick(
        sessionState.currentSessionId,
        sessionState.currentAgent,
        sessionState.currentMode,
        hasMessageAnchor(normalizedAnchor) ? normalizedAnchor : null,
      );
      const payload = {
        ...buildSystemCommandPayload('/rebuild', {
          sessionId: sessionState.currentSessionId,
          mode: sessionState.currentMode,
          agent: sessionState.currentAgent,
        }),
        rebuildAnchor: hasMessageAnchor(normalizedAnchor) ? normalizedAnchor : null,
      };
      send(payload);
      logAssistantRebuildUiSend(sessionState.currentSessionId, payload);
    }
  },
  truncateSessionForEdit: (anchor) => {
    if (!sessionState.currentSessionId) return;
    if (composeState.isGenerating || sessionState.currentSessionRunning) {
      appendError('当前会话正在处理中，请先停止或等待完成后再截断编辑。');
      return;
    }
    const normalizedAnchor = buildMessageAnchor(anchor);
    if (!hasMessageAnchor(normalizedAnchor)) {
      appendError('该消息缺少稳定锚点，无法截断编辑。');
      return;
    }
    const ok = window.confirm('这会立即删除该消息及之后的历史，并把该消息文本放入输入框。是否继续？');
    if (!ok) return;
    send({
      type: 'truncate_session_for_edit',
      sessionId: sessionState.currentSessionId,
      anchor: normalizedAnchor,
    });
  },
});
setupScrollbar();
setupGitHandlers({
  appendError: (msg, opts) => appendError(msg, opts),
  createOverlayPanel: (opts) => createOverlayPanel(opts),
  showOptionPicker: (title, opts, active, cb) => showOptionPicker(title, opts, active, cb),
  renderWorkspaceInsights: () => renderWorkspaceInsights(),
  buildWorkspaceActionButtons: (actions, options) => buildWorkspaceActionButtons(actions, options),
  nextLocalId: (prefix) => nextLocalId(prefix),
});
setupSessionManager({
  updateCwdBadge: () => updateCwdBadge(),
  renderWorkspaceInsights: () => renderWorkspaceInsights(),
  updateAgentScopedUI: () => updateAgentScopedUI(),
  setSelectedAgent: (agent, opts) => setSelectedAgent(agent, opts),
  setCurrentAgent: (agent) => setCurrentAgent(agent),
  getSavedModeForAgent: (agent) => getSavedModeForAgent(agent),
  renderMessages: (messages, opts) => renderMessages(messages, opts),
  ensureStreamingPlaceholderVisible: () => ensureStreamingPlaceholderVisible(),
  renderPendingAttachments: () => renderPendingAttachments(),
  highlightActiveSession: () => highlightActiveSession(),
  renderSessionList: () => renderSessionList(),
  closeSidebar: () => closeSidebar(),
  scrollToBottom: () => scrollToBottom(),
  buildWelcomeMarkup: (agent) => buildWelcomeMarkup(agent),
  setStatsDisplay: (msg) => setStatsDisplay(msg),
  getVisibleSessions: () => getVisibleSessions(),
  getSessionUpdatedTimestamp: (session) => getSessionUpdatedTimestamp(session),
  incrementRenderEpoch,
  clearPreviewCode,
  getSelectedAgent: () => selectedAgent,
});
setupSidebarHandlers({
  getProjects: () => projects,
  getSelectedAgent: () => selectedAgent,
  getSavedModeForAgent: (agent) => getSavedModeForAgent(agent),
  getSkipDeleteConfirm: () => skipDeleteConfirm,
  setPendingProjectSaveCallback: (cb) => { pendingProjectSaveCallback = cb; },
  openSession: (id, opts) => openSession(id, opts),
  resetChatView: (agent) => resetChatView(agent),
  renderWorkspaceInsights: () => renderWorkspaceInsights(),
  showDeleteConfirm: (agent, cb) => showDeleteConfirm(agent, cb),
  getVisibleSessions: () => getVisibleSessions(),
});
setupSettingsHandlers({
  getLoginOverlay: () => loginOverlay,
  getApp: () => app,
  getLoginError: () => loginError,
  getRememberPw: () => rememberPw,
  getLoginPassword: () => loginPassword,
  restoreInitialSession: (agent) => restoreInitialSession(agent),
  finishGenerating: () => finishGenerating(),
  getSelectedAgent: () => selectedAgent,
  getProjects: () => projects,
  getCurrentProjectSessionCount: (project) => getCurrentProjectSessionCount(project),
  getSavedModeForAgent: (agent) => getSavedModeForAgent(agent),
  createOverlayPanel: (opts) => createOverlayPanel(opts),
  getPendingPasswordChangeValue: () => pendingPasswordChangeValue,
  setPendingPasswordChangeValue: (value) => { pendingPasswordChangeValue = value; },
  getModelConfigCache: () => modelConfigCache,
  setModelConfigCache: (value) => { modelConfigCache = value; },
  getCodexConfigCache: () => codexConfigCache,
  setCodexConfigCache: (value) => { codexConfigCache = value; },
  getIsGenerating: () => isGenerating,
  setAuthToken: (value) => { authToken = value; },
});
setupConnectionHandlers({
  handleMessage: (msg) => handleServerMessage(msg),
  clearSessionLoading: () => clearSessionLoading(),
  appendError: (msg, opts) => appendError(msg, opts),
  getPendingAuthPassword: () => pendingAuthPassword,
});
setupMessageHandlers({
  connectionState,
  sessionState,
  composeState,
  loginOverlay,
  appEl: app,
  loginError,
  rememberPw,
  chatTitle,
  modeSelect,
  msgInput,
  costDisplay,
  sendBtn,
  abortBtn,
  openSession: (id, opts) => openSession(id, opts),
  resetChatView: (agent) => resetChatView(agent),
  restoreInitialSession: (agent) => restoreInitialSession(agent),
  renderSessionList: () => renderSessionList(),
  updateSessionItemLoadedMessageCount: (sessionId) => updateSessionItemLoadedMessageCount(sessionId),
  setCurrentSessionRunningState: (running) => setCurrentSessionRunningState(running),
  applySessionSnapshot: (snapshot, opts) => applySessionSnapshot(snapshot, opts),
  finalizeLoadedSession: (id) => finalizeLoadedSession(id),
  finishSessionSwitch: (id) => finishSessionSwitch(id),
  isBlockingSessionLoad: (id) => isBlockingSessionLoad(id),
  prependHistoryMessages: (msgs, opts) => prependHistoryMessages(msgs, opts),
  renderWorkspaceInsights: () => renderWorkspaceInsights(),
  renderGitPanel: () => renderGitPanel(),
  updateGitPanelBadge: () => updateGitPanelBadge(),
  appendError: (msg, opts) => appendError(msg, opts),
  appendSystemMessage: (msg) => appendSystemMessage(msg),
  clearSessionLoading: () => clearSessionLoading(),
  startGenerating: (source) => startGenerating(source),
  startNextStreamingTurn: () => startNextStreamingTurn(),
  finishGenerating: (id, source) => finishGenerating(id, source),
  renderStreamingSegments: (segs) => renderStreamingSegments(segs),
  updateStreamingAssistantMeta: (message) => updateStreamingAssistantMeta(message),
  appendToolCall: (id, name, input, done, kind, meta) => appendToolCall(id, name, input, done, kind, meta),
  updateToolCall: (id, result) => updateToolCall(id, result),
  flushRender: () => flushRender(),
  scheduleRender: () => scheduleRender(),
  markStreamingProcessTextSegments: () => markStreamingProcessTextSegments(),
  renderPendingAttachments: () => renderPendingAttachments(),
  autoResize: () => autoResize(),
  showForceChangePassword,
  restoreRememberedPasswordInput,
  saveRememberedPassword,
  clearRememberedPassword,
  handlePasswordChanged,
  handleForcedLogout,
  updateAgentScopedUI: () => updateAgentScopedUI(),
  showOptionPicker: (title, opts, active, cb) => showOptionPicker(title, opts, active, cb),
  showClaudeModelPicker: (entries, models, current, currentFull, onSelect) => showClaudeModelPicker(entries, models, current, currentFull, onSelect),
  getCodexModelOptions: () => getCodexModelOptions(),
  getOpenCodeModelOptions: () => getOpenCodeModelOptions(),
  getNativeSessions,
  getCodexSessions,
  getCwdSuggestions,
  getDirectoryListing,
  getPendingAuthPassword: () => pendingAuthPassword,
  setPendingAuthPassword: (v) => { pendingAuthPassword = v; },
  getSelectedAgent: () => selectedAgent,
  setModelConfigCache: (v) => { modelConfigCache = v; },
  setCodexConfigCache: (v) => { codexConfigCache = v; },
  setProjects: (v) => { projects = v; },
  getPendingProjectSaveCallback: () => pendingProjectSaveCallback,
  setPendingProjectSaveCallback: (v) => { pendingProjectSaveCallback = v; },
});

setSelectedAgent(selectedAgent, { syncMode: true });
resetChatView(selectedAgent);
connect();
appLifecycleController.bindGlobalLifecycleHandlers();
